// One conversation: the newest page stays live, older ones arrive as the
// visitor scrolls up, and the composer sends through the callable.
//
// The assistant's reply is not in the response to `chat-send` — a trigger
// writes it and the open listener delivers it a moment later, so the only
// thing rendered optimistically is the visitor's own message.

import {
  useState,
  useRef,
  useMemo,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ClipLoader from 'react-spinners/ClipLoader';
import { chatSend, chatMarkRead } from '../utils/firebase';
import { useChatMessages } from '../utils/hooks';
import { useDictation, isDictationSupported } from '../utils/dictation';
import { appendSpoken } from '../utils/transcribe';
import {
  chatTitle,
  senderName,
  isAssistantChat,
  assistantChatId,
  ASSISTANT_NAME,
} from '../utils/chat';
import { chatDayLabel, chatTimeShort } from '../utils/misc';
import { MessageTypes } from '../utils/enums';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import LoadMoreSentinel from './LoadMoreSentinel';
import './ChatThread.scss';

// How far from the bottom still counts as "reading the newest message", and so
// gets followed down when the next one lands
const STICK_TO_BOTTOM_PX = 60;

// The backend retries a reply for up to ten minutes, so an unanswered message
// older than this is not going to be answered
const TYPING_WINDOW_MS = 15 * 60 * 1000;

// Counted from chat-send's answer: while a message still says "Sending…", no
// reply to it can be on its way
const TYPING_DELAY_MS = 1500;

// An idle instance outlives this, so a ping inside it would be wasted
const WARM_TTL_MS = 5 * 60 * 1000;
// Per function, not per chat: every chat lands on the same chat-send, and
// only an assistant chat also wakes the assistant's trigger
const warmedAt = { send: 0, assistant: 0 };

const markWarm = (isAssistant) => {
  warmedAt.send = Date.now();
  if (isAssistant) warmedAt.assistant = warmedAt.send;
};

// Pays chat-send's cold start, and the assistant's wherever it is a member,
// while the visitor is still typing rather than after they press Send
const warm = async (chatId, isAssistant) => {
  const last = isAssistant ? warmedAt.assistant : warmedAt.send;
  if (Date.now() - last < WARM_TTL_MS) return;
  markWarm(isAssistant);
  try {
    await chatSend({ chatId, warmup: true });
  } catch {
    // Logged by the callable wrapper; a cold reply is only a slower one
  }
};

// The composer's cap, which dictated text is held to as well
const MESSAGE_MAX_LENGTH = 4000;

const DICTATION_HINTS = {
  starting: 'Starting the microphone…',
  listening: 'Listening…',
  stopping: 'Finishing…',
};

// The newest words are the ones being spoken, so a long stretch shows its end
const HEARD_TAIL = 140;
const heardTail = (text) =>
  text.length > HEARD_TAIL ? `…${text.slice(-HEARD_TAIL)}` : text;

const messageTypeClass = (type) =>
  Object.values(MessageTypes).includes(type) ? type : '';

// Keyed on the chat id by its parent, so the scroll anchor, the unsent text
// and the page cursor belong to one conversation and go with it. `docked`:
// mounted outside any modal, so on screen whenever mounted. `context` goes
// with every message sent.
export default function ChatThread({ chat, onBack, docked, context }) {
  const { user, usersById, viewedChatRef } = useFirebase();
  const { closeModal, isModalOpen } = useModal();
  const navigate = useNavigate();
  const uid = user?.id;
  const isAssistant = isAssistantChat(chat);
  const isShown = docked || isModalOpen;

  // A closed window is hidden, not unmounted, so the chat id is withheld while
  // it is — that stands the listener down and stops the mark-read below
  const { items, isSynced, isLoadingMore, hasMore, loadMore } = useChatMessages(
    isShown ? chat.id : null,
  );

  const [text, setText] = useState('');
  const [pending, setPending] = useState([]);
  // The sentinel needs the scroller as its observer root, and a ref alone
  // would not re-render it once the node exists
  const [scrollEl, setScrollEl] = useState(null);
  const [isAnchored, setIsAnchored] = useState(false);

  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  const atBottomRef = useRef(true);
  // Distance from the bottom, saved before an older page is asked for and
  // handed back once it has landed
  const restoreRef = useRef(null);
  const anchoredRef = useRef(false);
  // The oldest message on screen, which is how a prepended page is told from a
  // new one arriving at the bottom
  const topIdRef = useRef(null);
  // Sent one at a time: `createdAt` is stamped by the server, so two calls in
  // flight may land in either order, and the assistant reads them as stored
  const sendChainRef = useRef(Promise.resolve());
  // Messages handed to the chain and not confirmed yet, oldest first
  const unsentRef = useRef([]);

  // The viewer's own assistant chat only: one the assistant was added to is
  // still a chat between people
  const canDictate = chat.id === assistantChatId(uid) && isDictationSupported();
  const dictation = useDictation((spoken) =>
    setText((current) =>
      appendSpoken(current, spoken).slice(0, MESSAGE_MAX_LENGTH),
    ),
  );
  const isDictating = dictation.phase !== 'idle';
  const { stop: stopDictation } = dictation;

  // A hidden window keeps no microphone open; words still on their way land
  // in the composer, which keeps them for the next opening
  useEffect(() => {
    if (!isShown) stopDictation();
  }, [isShown, stopDictation]);

  const setScroll = useCallback((node) => {
    scrollRef.current = node;
    setScrollEl(node);
  }, []);

  // Everything the thread shows: what the listener has, plus the messages
  // whose write has not echoed back yet
  const messages = useMemo(() => {
    const known = new Set(items.map((m) => m.id));
    return [...items, ...pending.filter((m) => !m.id || !known.has(m.id))];
  }, [items, pending]);

  // Confirmed messages only: the optimistic one gets its id when chat-send
  // answers, and the listener's echo of it carries the same id
  const lastItem = messages.at(-1);
  const awaitedId =
    isAssistant && lastItem?.id && lastItem.senderId === uid
      ? lastItem.id
      : null;
  const lastAt = lastItem?.createdAt?.toMillis?.() ?? null;
  const [isInWindow, setIsInWindow] = useState(false);
  const [delayedId, setDelayedId] = useState(null);
  // Gated in render too: the reply that ends the wait hides the line on its own
  // frame rather than one effect later
  const isTyping = !!awaitedId && isInWindow && delayedId === awaitedId;

  // Keyed on the id alone: the echo brings the server's timestamp, which must
  // not restart the delay
  useEffect(() => {
    if (!awaitedId) return;
    const timer = setTimeout(() => setDelayedId(awaitedId), TYPING_DELAY_MS);
    return () => clearTimeout(timer);
  }, [awaitedId]);

  useEffect(() => {
    if (!awaitedId) {
      setIsInWindow(false);
      return;
    }
    // No server timestamp yet: the listener has not echoed the message back
    const left =
      lastAt === null
        ? TYPING_WINDOW_MS
        : lastAt + TYPING_WINDOW_MS - Date.now();
    if (left <= 0) {
      setIsInWindow(false);
      return;
    }
    setIsInWindow(true);
    const timer = setTimeout(() => setIsInWindow(false), left);
    return () => clearTimeout(timer);
  }, [awaitedId, lastAt]);

  const days = useMemo(() => {
    const groups = [];
    messages.forEach((message) => {
      const label = chatDayLabel(message.createdAt);
      const last = groups.at(-1);
      if (last?.label === label) last.messages.push(message);
      else groups.push({ label, messages: [message] });
    });
    return groups;
  }, [messages]);

  // A confirmed message is in `items` now, so its optimistic twin can go
  useEffect(() => {
    if (!pending.length) return;
    const known = new Set(items.map((m) => m.id));
    setPending((prev) => {
      const next = prev.filter((m) => !m.id || !known.has(m.id));
      return next.length === prev.length ? prev : next;
    });
  }, [items, pending.length]);

  // Three cases, all before paint so none of them is visible as a jump: the
  // first page lands at the bottom, an older page keeps the message the
  // visitor was reading under their eyes, and a new one is followed down only
  // if they were at the bottom to begin with.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !messages.length) return;

    // The anchor belongs to the page that is still on its way, so it is spent
    // on the render that actually prepends one: a message arriving at the
    // bottom meanwhile would otherwise consume it and leave the older page to
    // land unanchored
    const topId = items[0]?.id ?? null;
    const grewAtTop = topIdRef.current !== null && topId !== topIdRef.current;
    topIdRef.current = topId;

    if (restoreRef.current !== null && grewAtTop) {
      el.scrollTop = el.scrollHeight - restoreRef.current;
      restoreRef.current = null;
      return;
    }

    if (!anchoredRef.current) {
      el.scrollTop = el.scrollHeight;
      anchoredRef.current = true;
      setIsAnchored(true);
      return;
    }

    if (atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, items, isTyping]);

  // `resize: none` is global and there is no auto-growing field in the app, so
  // the composer measures itself. The cap is in the stylesheet.
  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX;
  };

  const loadOlder = useCallback(() => {
    // Armed only when a page is really coming: `loadMore` returns silently
    // once the history is exhausted, and an anchor nobody consumes would hold
    // the thread still the next time a message lands
    if (!hasMore || isLoadingMore) return;

    const el = scrollRef.current;
    restoreRef.current = el ? el.scrollHeight - el.scrollTop : null;
    loadMore();
  }, [loadMore, hasMore, isLoadingMore]);

  // A message after the watermark may leave hasUnread true throughout, so the
  // mark-read also follows the last message's time
  const lastMessageMs = chat.lastMessageAt?.toMillis?.() ?? null;

  // Read on opening, and again whenever a message arrives while the window is
  // open — otherwise the badge lights up over a chat somebody is reading.
  // Nothing unread is nothing to move: a watermark already ahead of the last
  // message would only cost a callable and a write on every open.
  useEffect(() => {
    if (!isShown || !chat.id || !chat.hasUnread) return;

    const markRead = async () => {
      try {
        await chatMarkRead({ chatId: chat.id });
      } catch {
        // Logged by the callable wrapper. An account older than the
        // assistant-chat backfill has none to mark, which is nothing to tell
        // the visitor about.
      }
    };

    markRead();
  }, [isShown, chat.id, chat.hasUnread, lastMessageMs]);

  // On screen on the same terms as the mark-read above. One entry per thread:
  // the schedule's panel and the Chats window may show the same chat at once
  useEffect(() => {
    if (!isShown) return;
    const viewed = viewedChatRef.current;
    viewed.push(chat.id);
    return () => viewed.splice(viewed.indexOf(chat.id), 1);
  }, [isShown, chat.id, viewedChatRef]);

  useEffect(() => {
    if (isShown) warm(chat.id, isAssistant);
  }, [isShown, chat.id, isAssistant]);

  // A window left open past the ping's lifetime warms up again here
  const onChange = (event) => {
    setText(event.target.value);
    warm(chat.id, isAssistant);
  };

  // Pressing the microphone means a message is coming
  const startDictation = () => {
    warm(chat.id, isAssistant);
    dictation.start();
  };

  const send = async () => {
    const body = text.trim();
    // Held while dictating: the last words are still on their way
    if (!body || isDictating) return;
    // A message wakes the same instances a ping would
    markWarm(isAssistant);

    const tempId = `pending_${Date.now()}`;
    setPending((prev) => [
      ...prev,
      { tempId, text: body, senderId: uid, createdAt: new Date() },
    ]);
    setText('');
    atBottomRef.current = true;

    // Behind the previous send: a failure rejects everything queued after it,
    // or a retry of the failed text would land behind its successors
    unsentRef.current.push({ tempId, body });
    const sending = sendChainRef.current.then(() =>
      chatSend({ chatId: chat.id, text: body, ...(context && { context }) }),
    );
    sendChainRef.current = sending;

    try {
      const { data } = await sending;
      unsentRef.current = unsentRef.current.filter((m) => m.tempId !== tempId);
      setPending((prev) =>
        prev.map((m) => (m.tempId === tempId ? { ...m, id: data.id } : m)),
      );
    } catch (error) {
      // Already handed back by the failure that cancelled it
      if (!unsentRef.current.some((m) => m.tempId === tempId)) return;
      const unsent = unsentRef.current;
      unsentRef.current = [];
      sendChainRef.current = Promise.resolve();

      const tempIds = new Set(unsent.map((m) => m.tempId));
      setPending((prev) => prev.filter((m) => !tempIds.has(m.tempId)));
      // Handed back rather than lost, but only into a composer still empty:
      // whatever was typed while the send was in flight outranks it
      setText((current) => current || unsent.map((m) => m.body).join('\n'));
      toast.error(error?.message ?? 'Failed to send the message');
    }
  };

  const onKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    // An IME commits its candidate with the same key, and sending there would
    // post half a word
    if (event.nativeEvent?.isComposing) return;
    event.preventDefault();
    send();
  };

  const openLink = (url) => {
    closeModal();
    navigate(url);
  };

  const renderMessage = (message) => {
    const isMine = message.senderId === uid;
    const typeClass = messageTypeClass(message.type);

    return (
      <li
        className={'bubble' + (isMine ? ' mine' : '')}
        key={message.id ?? message.tempId}
      >
        {!isMine && (
          <p className='sender font-12'>{senderName(message, usersById)}</p>
        )}
        {message.title && (
          <h6 className={'title ' + typeClass}>{message.title}</h6>
        )}
        {message.text && <p className='text'>{message.text}</p>}
        {/* In-app relative links only — external urls are not navigated */}
        {message.url?.startsWith('/') && (
          <button
            className='small open-link'
            onClick={() => openLink(message.url)}
          >
            Open
          </button>
        )}
        <p className='time font-12'>
          {message.tempId && !message.id
            ? 'Sending…'
            : chatTimeShort(message.createdAt)}
        </p>
      </li>
    );
  };

  return (
    <div className='chat-thread'>
      <div className='thread-top'>
        <button
          className='back transparent small icon fa-left-arrow'
          onClick={onBack}
          aria-label='Back to chats'
        />
        <h6 className='name'>{chatTitle(chat, uid, usersById)}</h6>
      </div>

      <div className='thread-body' ref={setScroll} onScroll={onScroll}>
        {/* Held back until the first page is anchored at the bottom: on the
            frame before that the sentinel sits in view and would ask for the
            whole history at once */}
        {isAnchored && (
          <LoadMoreSentinel
            hasMore={hasMore}
            isLoading={isLoadingMore}
            onLoadMore={loadOlder}
            root={scrollEl}
            rootMargin='200px'
          />
        )}

        {!messages.length && !isSynced && (
          <div className='thread-loading'>
            <ClipLoader size={24} color='#3f90dc' />
          </div>
        )}

        {/* A conclusion from absence, so it waits for the server */}
        {!messages.length && isSynced && (
          <p className='empty font-16'>No messages yet</p>
        )}

        {days.map((day) => (
          <div className='day' key={day.label}>
            <h6 className='day-label'>{day.label}</h6>
            <ul className='bubbles'>{day.messages.map(renderMessage)}</ul>
          </div>
        ))}

        {isTyping && (
          <p className='typing font-12'>{ASSISTANT_NAME} is typing…</p>
        )}
      </div>

      <div className='composer'>
        {isDictating && (
          <p className='heard font-12' role='status'>
            {heardTail(dictation.partial) || DICTATION_HINTS[dictation.phase]}
          </p>
        )}
        <div className='compose-row'>
          <textarea
            ref={composerRef}
            value={text}
            rows={1}
            maxLength={MESSAGE_MAX_LENGTH}
            placeholder='Write a message'
            onChange={onChange}
            onKeyDown={onKeyDown}
          />
          {canDictate && (
            <button
              className={
                'small mic ' +
                (isDictating ? 'danger fa-stop' : 'bw fa-microphone')
              }
              onClick={isDictating ? dictation.stop : startDictation}
              disabled={dictation.phase === 'stopping'}
              aria-label={isDictating ? 'Stop dictating' : 'Dictate a message'}
              title={isDictating ? 'Stop dictating' : 'Dictate a message'}
            />
          )}
          <button
            className='small send'
            onClick={send}
            disabled={!text.trim() || isDictating}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
